// Field-data load phase. Initialises the fieldline worker once we have
// component arrays, builds the fieldline-core THREE adapter, and seeds the
// initial fieldlines from scene config. Per-frame rendering is paused while
// the seed runs; this is the slow startup path on cold load.

import * as THREE from 'three';
import { params, store } from '@store/instance';
import { INTENTS } from '@store/intents';
import { createFieldlineCore } from '@adapters/fieldlines';
import { initFieldlineWorker } from '@workers/fieldlineClient';
import { seedFromConfig as seedFieldlines } from '@scene/fieldlines';
import { debugWarn } from '@adapters/debug';
import type { ViewerContext } from '../context';

export async function wireFieldDependentSteps(viewer: ViewerContext): Promise<void> {
    const { camera, renderer, scene } = viewer.three;

    if (typeof params.fieldComponent === 'string') {
        try { store.dispatch(INTENTS.SET_FIELD_COMPONENT, { component: params.fieldComponent }); }
        catch (e) { debugWarn(viewer, 'Initial fieldComponent dispatch failed:', e); }
    }

    try {
        await initFieldlineWorker(
            viewer.grid,
            viewer.field.data?.['Bx'] ?? null,
            viewer.field.data?.['By'] ?? null,
            viewer.field.data?.['Bz'] ?? null,
        );
    } catch { /* worker optional */ }

    viewer.objects.fieldlines.core = createFieldlineCore({
        THREE,
        grid: viewer.grid,
        getFieldArrays: () => ({
            Bx: viewer.field.data?.['Bx'] ?? null,
            By: viewer.field.data?.['By'] ?? null,
            Bz: viewer.field.data?.['Bz'] ?? null,
        }),
    }) as unknown as ViewerContext['objects']['fieldlines']['core'];

    await seedFieldlines(viewer, params, {
        showProgress: true,
        onAfterEach: () => { if (renderer && scene && camera) renderer.render(scene, camera); },
    });
}
